<script setup lang="ts">
import { ref } from 'vue';
import type { Treatment } from '../../types/treatment';
import BookingModal from './BookingModal.vue';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const props = defineProps<{
  treatments: Treatment[];
  type: 'massage' | 'yogatherapy';
}>();

const showBookingModal = ref(false);

function formatDate(dateStr: string): string {
  return format(parseISO(dateStr), 'dd. MMMM yyyy', { locale: de });
}

const emit = defineEmits<{
  (e: 'book', data: any): void;
}>();
</script>

<template>
  <div class="treatment-list">
    <div v-if="treatments.length === 0" class="no-treatments">
      Keine {{ type === 'massage' ? 'Massagen' : 'Yogatherapie-Termine' }} gefunden
    </div>
    
    <template v-else>
      <div v-for="treatment in treatments" :key="`${treatment.date}-${treatment.time}`" class="treatment-item">
        <div class="treatment-header">
          <div class="date-time">
            {{ formatDate(treatment.date) }} | {{ treatment.time }}
          </div>
          <div class="status" :class="treatment.status">
            {{ treatment.status }}
          </div>
        </div>
        
        <div class="treatment-details">
          <div class="type">{{ treatment.type }}</div>
          <div class="therapist" v-if="treatment.therapist">
            {{ treatment.therapist }}
          </div>
          <div class="room">{{ treatment.room }}</div>
        </div>
      </div>
    </template>

    <div class="button-container">
      <button class="book-button" @click="showBookingModal = true">
        {{ type === 'massage' ? 'Massage buchen' : 'Yogatherapie buchen' }}
      </button>
    </div>

    <BookingModal
      :type="type"
      :is-open="showBookingModal"
      @close="showBookingModal = false"
      @submit="emit('book', $event)"
    />
  </div>
</template>

<style scoped>
.treatment-list {
  padding: 1rem;
  padding-bottom: 120px;
}

.no-treatments {
  text-align: center;
  color: #666;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.treatment-item {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.treatment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.date-time {
  color: #960040;
  font-weight: 500;
}

.status {
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.875rem;
}

.status.confirmed {
  background: #dcfce7;
  color: #166534;
}

.status.pending {
  background: #fef9c3;
  color: #854d0e;
}

.status.cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.treatment-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.type {
  font-weight: 500;
}

.therapist, .room {
  color: #666;
  font-size: 0.9rem;
}

.button-container {
  position: fixed;
  bottom: 62px;
  left: 0;
  right: 0;
  padding: 1rem;
  display: flex;
  justify-content: center;
  background: linear-gradient(to top, rgba(245, 245, 245, 1) 80%, rgba(245, 245, 245, 0));
}

.book-button {
  background: #960040;
  color: white;
  padding: 1rem 2rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.book-button:hover {
  background: #7d0035;
}
</style>